import { useState, type ChangeEvent, type ReactNode } from 'react';
import type { CargoCheck } from '@/api/client';
import { Button } from '@/components/Button';
import { CrewActionBar, CrewIcon, CrewMessage } from '@/components/CrewUi';
import { compressImage, EVIDENCE_COMPRESS_OPTIONS } from '@/lib/imageCompression';
import {
  CARGO_COPY,
  CARGO_REASON_CHIPS,
  buildCargoCheckBody,
  isCountMismatch,
  type CargoMismatchReason,
  type CargoRequestBody,
} from '@/lib/crewCargoCheck';

/**
 * "Confirm cargo" step for a stop without a manifest (between Start servicing and Mark
 * serviced). Shared by the Trip and Scan screens. piece_count: booked count + one-tap
 * "all here" or a different count with a reason; pic_confirmation: the person in charge
 * (PIC) confirmed all cargo loaded/unloaded. The PIC name is required in both modes.
 * The primary action sits in the sticky bottom action bar (one thumb, gloves).
 */
export function CrewCargoCheckPanel(props: {
  stopType: string;
  cargoCheck: CargoCheck;
  defaultPicName?: string;
  busy: boolean;
  onSubmit: (body: CargoRequestBody) => Promise<void> | void;
  /** Optional line under the title (e.g. the Scan screen's "No manifest for this stop…"). */
  intro?: ReactNode;
  /** Optional secondary action shown under the primary in the action bar (e.g. Cancel). */
  barSecondary?: ReactNode;
}) {
  const { cargoCheck, busy } = props;
  const expected = cargoCheck.expectedPieces;
  const pieceMode = cargoCheck.mode === 'piece_count';
  const [picName, setPicName] = useState(props.defaultPicName ?? '');
  const [differentCount, setDifferentCount] = useState(pieceMode && expected == null);
  const [counted, setCounted] = useState('');
  const [reason, setReason] = useState<CargoMismatchReason | ''>('');
  const [note, setNote] = useState('');
  const [photo, setPhoto] = useState('');
  const [photoLoading, setPhotoLoading] = useState(false);
  const [picConfirmed, setPicConfirmed] = useState(false);
  const [error, setError] = useState('');

  const mismatch = pieceMode && differentCount && isCountMismatch(expected, counted);
  const allHereMode = pieceMode && !differentCount && expected != null;

  const submit = async (allHere: boolean) => {
    if (busy || photoLoading) return;
    const result = buildCargoCheckBody({
      mode: cargoCheck.mode,
      expectedPieces: expected,
      picName,
      allHere,
      counted,
      reason,
      note,
      photo,
      picConfirmed,
    });
    if ('error' in result) {
      setError(result.error);
      return;
    }
    setError('');
    await props.onSubmit(result.body);
  };

  const handlePhoto = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoLoading(true);
    try {
      setPhoto(await compressImage(file, EVIDENCE_COMPRESS_OPTIONS));
    } catch {
      setPhoto('');
      setError('Could not read photo — try another image.');
    } finally {
      setPhotoLoading(false);
    }
  };

  const step = (delta: number) => {
    const current = /^\d+$/.test(counted.trim()) ? Number(counted.trim()) : (expected ?? 0);
    setCounted(String(Math.max(0, current + delta)));
  };

  const primaryLabel = allHereMode
    ? CARGO_COPY.allHere(expected as number)
    : pieceMode
      ? CARGO_COPY.saveCount
      : CARGO_COPY.confirmPic;

  return (
    <section className="crew-card crew-card-hl crew-cargo-check" aria-label={CARGO_COPY.title}>
      <span className="crew-kicker">{CARGO_COPY.title}</span>
      {props.intro && <p className="cargo-intro">{props.intro}</p>}
      {pieceMode && expected != null && <div className="cargo-booked crew-num">{CARGO_COPY.booked(expected)}</div>}

      <label className="cargo-field">
        <span className="crew-field-label">{CARGO_COPY.picNameLabel}</span>
        <input
          className="crew-input"
          type="text"
          value={picName}
          onChange={(e) => setPicName(e.target.value)}
          placeholder={CARGO_COPY.picNamePlaceholder}
          autoComplete="name"
          maxLength={200}
        />
      </label>

      {pieceMode ? (
        <>
          {allHereMode && (
            <Button type="button" variant="secondary" fullWidth onClick={() => {
              setDifferentCount(true);
              if (!counted && expected != null) setCounted(String(expected));
            }}>
              {CARGO_COPY.differentCount}
            </Button>
          )}
          {differentCount && (
            <>
              <div className="cargo-field">
                <label className="crew-field-label" htmlFor="cargo-counted">{CARGO_COPY.countedLabel}</label>
                <div className="cargo-stepper">
                  <button type="button" className="cargo-step" aria-label="One less" onClick={() => step(-1)}>−</button>
                  <input
                    id="cargo-counted"
                    className="crew-input crew-num"
                    type="number"
                    inputMode="numeric"
                    min={0}
                    step={1}
                    value={counted}
                    onChange={(e) => setCounted(e.target.value)}
                  />
                  <button type="button" className="cargo-step" aria-label="One more" onClick={() => step(1)}>+</button>
                </div>
              </div>
              {mismatch && expected != null && (
                <>
                  <CrewMessage kind="warn">{CARGO_COPY.mismatchHint(expected)}</CrewMessage>
                  <span className="crew-field-label">{CARGO_COPY.reasonLabel}</span>
                  <div className="cargo-chips" role="group" aria-label={CARGO_COPY.reasonLabel}>
                    {CARGO_REASON_CHIPS.map((c) => (
                      <button
                        key={c.reason}
                        type="button"
                        className={`cargo-chip ${reason === c.reason ? 'active' : ''}`}
                        aria-pressed={reason === c.reason}
                        onClick={() => setReason(c.reason)}
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                  <label className="cargo-field">
                    <span className="crew-field-label">{CARGO_COPY.noteLabel}</span>
                    <input
                      className="crew-input"
                      type="text"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={CARGO_COPY.notePlaceholder}
                      maxLength={1000}
                    />
                  </label>
                  <div className="cargo-field">
                    <span className="crew-field-label">{CARGO_COPY.photoLabel}</span>
                    <label className={`crew-photo-btn ${photoLoading || busy ? 'is-disabled' : ''}`}>
                      <CrewIcon name="camera" />
                      {photo ? 'Retake photo' : 'Take photo'}
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        aria-label={CARGO_COPY.photoLabel}
                        onChange={(e) => void handlePhoto(e)}
                        disabled={photoLoading || busy}
                      />
                    </label>
                    {photoLoading && <span className="crew-photo-status waiting">Preparing photo…</span>}
                    {!photoLoading && photo && (
                      <span className="crew-photo-status">
                        <span>Photo attached</span>
                        <button type="button" className="cargo-remove" onClick={() => setPhoto('')}>Remove</button>
                      </span>
                    )}
                  </div>
                </>
              )}
              {expected != null && (
                <button
                  type="button"
                  className="crew-link-btn"
                  onClick={() => {
                    setDifferentCount(false);
                    setCounted('');
                    setReason('');
                  }}
                >
                  {CARGO_COPY.sameCount}
                </button>
              )}
            </>
          )}
        </>
      ) : (
        <label className={`cargo-check-row ${picConfirmed ? 'checked' : ''}`}>
          <input
            type="checkbox"
            checked={picConfirmed}
            onChange={(e) => setPicConfirmed(e.target.checked)}
          />
          <span>{props.stopType === 'drop' ? CARGO_COPY.picConfirmUnloaded : CARGO_COPY.picConfirmLoaded}</span>
        </label>
      )}

      {error && <CrewMessage kind="error">{error}</CrewMessage>}

      <CrewActionBar>
        <Button
          type="button"
          variant="crew"
          size="crew"
          fullWidth
          loading={busy}
          disabled={busy || photoLoading}
          onClick={() => void submit(allHereMode)}
        >
          {primaryLabel}
        </Button>
        {props.barSecondary}
      </CrewActionBar>

      <style>{`
        .crew-cargo-check { display: flex; flex-direction: column; gap: var(--crew-gap); }
        .crew-cargo-check .crew-msg { margin: 0; }
        .crew-cargo-check .crew-field-label { margin: 0; }
        .cargo-intro { margin: 0; font-size: var(--crew-fs-body); font-weight: 600; }
        .cargo-booked { font-size: var(--crew-fs-title); font-weight: 700; line-height: 1.2; }
        .cargo-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
        .cargo-stepper { display: grid; grid-template-columns: var(--crew-btn) minmax(0, 1fr) var(--crew-btn); gap: var(--cs-2); }
        .cargo-step {
          min-height: var(--crew-btn);
          border-radius: var(--crew-r);
          border: 2px solid var(--crew-muted);
          background: transparent;
          color: var(--crew-white);
          font-size: 1.75rem;
          font-weight: 700;
          line-height: 1;
          cursor: pointer;
        }
        .cargo-chips { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 120px), 1fr)); gap: var(--cs-2); }
        .cargo-chip {
          min-height: var(--crew-touch);
          padding: var(--cs-1) var(--cs-3);
          border-radius: var(--crew-r);
          border: 2px solid var(--crew-outline);
          background: var(--crew-raised);
          color: var(--crew-white);
          font-size: var(--crew-fs-body);
          font-weight: 600;
          cursor: pointer;
        }
        .cargo-chip.active { background: var(--crew-signal); color: var(--crew-bg); border-color: var(--crew-signal); font-weight: 700; }
        .cargo-remove {
          min-height: 44px;
          padding: 0 var(--cs-3);
          background: none;
          border: none;
          color: var(--crew-white);
          font-weight: 700;
          font-size: var(--crew-fs-support);
          cursor: pointer;
        }
        .cargo-check-row {
          display: flex;
          align-items: center;
          gap: var(--cs-3);
          min-height: var(--crew-btn-big);
          padding: var(--cs-3) var(--cs-4);
          border-radius: var(--crew-r);
          border: 2px solid var(--crew-outline);
          background: var(--crew-raised);
          font-weight: 700;
          font-size: var(--crew-fs-body);
          cursor: pointer;
        }
        .cargo-check-row.checked { border-color: var(--crew-ok-bright); background: #22483A; }
        .cargo-check-row input { width: 28px; height: 28px; flex-shrink: 0; margin: 0; }
      `}</style>
    </section>
  );
}
